<script setup>
import { ref, watch } from 'vue';
import { formatNumber } from '@/composables/functions';
import { usePaymentsStore } from '@/store/modules/payments';
import { useBackLayer } from '@/composables/useAppBack';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  analysisType: {
    type: Object,
    default: null,
  },
  ticketCountData: {
    type: Object,
    default: null,
  },
  ticketCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['close', 'charge']);

const paymentsStore = usePaymentsStore();

const isDialogVisible = ref(props.isVisible);

const isMobile = computed(() => window.ReactNativeWebView);

const shortageAmount = computed(() => {
  return props.analysisType?.pointPrice - paymentsStore.points;
});

const discountText = computed(() => {
  if (!props.analysisType) return '';
  return `${
    Math.round(((props.analysisType.pointBasePrice - props.analysisType.pointPrice) /
      props.analysisType.pointBasePrice) *
    100)
  }% 할인`;
});

watch(
  () => props.isVisible,
  async (newVal) => {
    isDialogVisible.value = newVal;
    // 팝업이 열릴 때 포인트 정보를 다시 가져오기
    if (newVal) {
      await paymentsStore.getOwnPoint();
    }
  },
);

const closePopup = () => {
  emit('close');
};

useBackLayer({
  id: 'analysis-insufficient-points',
  isOpen: computed(() => props.isVisible),
  closeFromBack: closePopup,
});

const goCharge = () => {
  emit('charge', shortageAmount.value);
};
</script>

<template>
  <div v-if="isDialogVisible" class="pop-analysis-info-backdrop">
    <div class="pop-analysis-info" :class="{ mobile: isMobile }">
      <div class="pop-header">
        <div class="pop-title">먼저 포인트를 충전해 주세요!</div>
        <button class="pop-close" @click="closePopup"></button>
      </div>

      <div class="pop-desc main">
        모든 분석 서비스는 <span class="underlined-text">포인트 충전 후</span><br />
        바로 이용하실 수 있습니다.
      </div>

      <div class="pop-options">
        <div class="option-box large">
          <div class="option-icon-box">
            <div v-if="discountText" class="option-discount">{{ discountText }}</div>
            <div class="option-label">총 결제할 포인트</div>
          </div>
          <div class="option-price__box">
            <div class="option-base-price">{{ formatNumber(analysisType?.pointBasePrice) }} P</div>
            <div class="option-price-arrow"></div>
            <div class="option-price">{{ formatNumber(analysisType?.pointPrice) }} P</div>
          </div>
        </div>
        <div class="option-box">
          <div class="option-icon-box">
            <div class="option-label">보유 포인트</div>
          </div>
          <div class="option-price">{{ paymentsStore.formattedPoints || '0' }} P</div>
        </div>
        <div class="option-box">
          <div class="option-icon-box">
            <div class="option-label">부족한 포인트</div>
          </div>
          <div class="option-price">{{ formatNumber(shortageAmount) }} P</div>
        </div>
      </div>

      <div class="pop-description">
        <div class="pop-description__text">
          <div class="pop-description__icon"></div>
          2건 이상 구매하실 경우 충전 후
          <span class="bold-text">[서비스구매]</span>
          에서 보다 할인된 분석권으로 알뜰하게 이용하실 수 있어요.
        </div>
      </div>

      <button class="paywall_charge_button_charge" @click="goCharge">포인트 충전하러 가기</button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pop-analysis-info-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pop-analysis-info {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  width: 400px;
  max-width: calc(100vw - 32px);
  padding: 32px 24px 24px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}
.pop-header {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
  .pop-close {
    position: absolute;
    right: 24px;
    top: 30px;
  }
}
.pop-title {
  color: var(--Gray-800, #333);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
}
.pop-close {
  background: url('@manseImages/icon_close_1.svg');
  background-size: 24px 24px;
  border: none;
  width: 24px;
  height: 24px;
  cursor: pointer;
}
.pop-desc.main {
  font-size: 14px;
  color: #333;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 400;
  line-height: 24px;
  .underlined-text {
    font-weight: 700;
    border-bottom: 1px solid #333;
    // text-decoration-line: underline;
    // text-decoration-style: solid;
    // text-decoration-skip-ink: none;
    // text-decoration-thickness: auto;
    // text-underline-offset: auto;
    // text-underline-position: from-font;
  }
}
.pop-highlight {
  color: var(--Gray-800, #333);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 160% */
  margin-bottom: 12px;
}
.pop-options {
  width: 100%;
  margin-bottom: 20px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: var(--Orange-100, #fff8f5);
  border-radius: 12px;
}
.option-box {
  // flex: 1;
  padding: 8px 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: transparent !important;
  border-radius: 0 !important;
  border: none !important;
  // &:first-child {
  //   border-bottom: 1px solid var(--Gray-300, #ebebeb);
  // }
  .option-icon-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .option-icon.point {
    width: 32px;
    height: 32px;
    background-size: 32px auto;
    background-image: url('@manseImages/icon_an_guide_point.png');
  }

  .option-icon.ticket {
    width: 32px;
    height: 32px;
    background-image: url('@manseImages/icon_an_guide_Frame.png');
    background-size: 32px auto;
  }
}
.option-discount {
  display: flex;
  height: 18px;
  padding: 3px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: #eb4c10;
  color: #fff;
  text-align: center;
  font-family: Pretendard;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px; /* 120% */
}
.option-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--Gray-800, #333);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
}
.option-box.large {
  margin-bottom: 12px;
  align-items: flex-end;
  .option-label {
    font-size: 16px;
  }
}
.option-desc {
  color: var(--Gray-700, #575757);
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 138.462% */
}
.option-price__box {
  display: flex;
  flex-direction: row;
  gap: 2px;
}
.option-base-price {
  color: var(--Gray-600, #757575);
  text-align: center;
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 184.615% */
  text-decoration-line: line-through;
}
.option-price-arrow {
  width: 24px;
  height: 24px;
  background: url('@manseImages/icon_arrow_right.svg');
}
.option-price {
  color: #eb4c10;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}
.option-sale {
  font-size: 13px;
  font-weight: 400;
  color: #eb4c10;
  margin-right: 2px;
}
.option-unit {
  font-size: 13px;
  font-weight: 400;
  margin-left: 2px;
}
.badge {
  display: inline-flex;
  height: 18px;
  padding: 3px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: rgba(161, 105, 200, 0.1);
  color: #a169c8;
  text-align: center;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 12px; /* 120% */
}
.pop-description {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 14px 16px;
  margin-bottom: 22px;
  border-radius: 6px;
  background: #f8f8f8;
  word-break: keep-all;
  .pop-description__icon {
    width: 16px;
    height: 16px;
    background: url('@manseImages/icon_reference_mark.svg') center center no-repeat;
    flex-shrink: 0;
    margin-right: 4px;
  }
  .pop-description__text {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    // flex-direction: row;
    color: var(--gray-900-black, #2c2c2e);
    text-align: center;
    font-family: Pretendard;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    .bold-text {
      font-size: 14px;
      font-weight: 800;
      margin-left: 4px;
    }
  }
}
.paywall_charge_button_charge {
  width: 100%;
  background: #eb4c10;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  padding: 14px 0;
  cursor: pointer;
  /* transition: background 0.2s; */
}
/* .pop-charge-btn:hover {
  background: #d13e0a;
} */
.pop-analysis-info.mobile {
  max-width: calc(100% - 32px);
  gap: 12px;
  padding: 20px;
  .pop-close {
    top: 20px;
    right: 20px;
  }
  .pop-header,
  .pop-desc.main,
  .pop-options,
  .pop-description {
    margin-bottom: 0;
  }
  .paywall_charge_button_charge {
    margin-top: 8px;
  }
}

</style>
